import { Box, Stack } from "@mui/material";
import React, { useMemo, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BannerStatus } from "@swagger/typescript-fetch-goflyer";

import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { adminApiService } from "../../../utils/api";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { messages } from "../../../locales/messages";
import { toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { SetupPageTopAppBar } from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import { LeftButtonSection, TableWrapper, Container } from "./styled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useLoading } from "../../../contextApi/LoadingContext";
import { useBannerTableColumns } from "./useBannerTableColumns";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { routes } from "../../../utils/constant/navigationRoutes";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { BannerRow } from "../../../utils/banner/bannerRow";
import {
  SUPPORTED_BANNER_PLACEMENTS,
  enumWireValues,
} from "../../../utils/banner/bannerFields";
import SelectMui from "../../components/SelectMui";

const ALL = "";

const BannerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();
  const [searchParams, setSearchParams] = useSearchParams();

  const { paginationModel, sortModel, handleTableChange } = useQueryParams();
  const listGuard = useLatestRequestGuard();

  const [paginationLoading, setPaginationLoading] = useState(false);
  const [bannerListData, setBannerListData] = useState<TableData<BannerRow>>();
  const [open, setOpen] = useState(false);
  const [bannerToBeDeleted, setBannerToBeDeleted] = useState<BannerRow>();
  const { showLoadingScreen } = useLoading();

  const placementFilter = searchParams.get("placement") || ALL;
  const statusFilter = searchParams.get("status") || ALL;

  const handleOpenDelete = (value: BannerRow) => {
    setBannerToBeDeleted(value);
    setOpen(true);
  };

  const columns = useBannerTableColumns({ handleOnDelete: handleOpenDelete });

  const handleCancel = () => {
    setOpen(false);
    setBannerToBeDeleted(undefined);
  };

  const placementOptions = useMemo(
    () => [
      { label: t(messages.banner_all_placements()), value: ALL },
      ...SUPPORTED_BANNER_PLACEMENTS.map((value) => ({
        label: value,
        value,
      })),
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => [
      { label: t(messages.banner_all_statuses()), value: ALL },
      ...enumWireValues(BannerStatus).map((value) => ({
        label: value,
        value,
      })),
    ],
    [t],
  );

  const applyFilter = (key: "placement" | "status", value: string) => {
    const next = new URLSearchParams(searchParams.toString());
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  const fetchBannerList = async () => {
    const filter: string[] = [];
    if (placementFilter) filter.push(`placement||$eq||${placementFilter}`);
    if (statusFilter) filter.push(`status||$eq||${statusFilter}`);

    await guardedListFetch(
      listGuard,
      setPaginationLoading,
      () =>
        adminApiService.getManyBaseGfBannerAdminControllerGfBanner(
          undefined,
          undefined,
          filter.length ? filter : undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : ["priority,DESC", "updatedAt,DESC"],
          ["createdByAdmin", "updatedByAdmin", "desktopImage", "mobileImage"],
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
          authHeaders,
        ),
      (bannerList) =>
        setBannerListData((prev) => ({
          ...prev,
          rows: bannerList.data as BannerRow[],
          total: bannerList?.total,
        })),
    );
  };

  const handleDeleteBanner = async () => {
    if (!bannerToBeDeleted?.id) return;

    showLoadingScreen(true);
    const result =
      await adminApiService.deleteOneBaseGfBannerAdminControllerGfBanner(
        bannerToBeDeleted.id,
        authHeaders,
      );
    showLoadingScreen(false);

    if (result === undefined) return;

    handleCancel();
    toastSuccess(t(messages.Successfully_Deleted()));
    fetchBannerList();
  };

  React.useEffect(() => {
    setBannerListData((prev) => ({ ...prev, columns }));
  }, [t]);

  React.useEffect(() => {
    fetchBannerList();
  }, [placementFilter, statusFilter, paginationModel, sortModel]);

  return (
    <Container data-testid="banner-page-test-id">
      <AccountCardWrapper title={t(messages.Banner())}>
        <Box data-testid="view-page">
          <SetupPageTopAppBar>
            <LeftButtonSection>
              <Stack direction="row" spacing={2} alignItems="center">
                <PermissionComponentWrapper
                  requiredPermission={["create"]}
                  requiredPermissionGroup={["banner"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <UploadFileIcon
                        style={{ color: "#ff8038", fontSize: 25 }}
                      />
                    }
                    onClick={() => {
                      navigate(routes.banner.create);
                    }}
                    data-testid="create-button"
                  >
                    {t(messages.Create())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>

                <SelectMui<string>
                  title={t(messages.banner_placement())}
                  selectOptions={placementOptions}
                  selectedValue={placementFilter}
                  onSelect={(value) => applyFilter("placement", value)}
                  FormControlStyle={{ width: 200 }}
                  dataTestId="banner-placement-filter"
                />

                <SelectMui<string>
                  title={t(messages.banner_status())}
                  selectOptions={statusOptions}
                  selectedValue={statusFilter}
                  onSelect={(value) => applyFilter("status", value)}
                  FormControlStyle={{ width: 160 }}
                  dataTestId="banner-status-filter"
                />
              </Stack>
            </LeftButtonSection>
          </SetupPageTopAppBar>
          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listGuard.loadFailed}
              columns={bannerListData?.columns}
              rows={bannerListData?.rows}
              total={paginationLoading ? 0 : bannerListData?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={230}
            />
          </TableWrapper>

          <MuiConfirmationModal
            open={open}
            handleCancel={handleCancel}
            handleConfirm={handleDeleteBanner}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())}?  ${t(
          messages.This_action_cannot_be_undone(),
        )}.
      `}
          />
        </Box>
      </AccountCardWrapper>
    </Container>
  );
};

export default BannerPage;
